/**
 * Impermissible uses and breaches, inside Precision Privacy. Mounts the
 * incident screens exactly as their own container does (same components,
 * same props, same search behavior) but under the program's single
 * navigation: sub-tabs instead of the second rail, no second header.
 * The files under src/pages/Privacy are not modified.
 */
import React, { useEffect, useState } from 'react';
import PrivacyWorkPlan from '../Privacy/PrivacyWorkPlan';
import PrivacyDashboard from '../Privacy/PrivacyDashboard';
import PrivacyIncidentDetail from '../Privacy/PrivacyIncidentDetail';
import PrivacyLawLibrary from '../Privacy/PrivacyLawLibrary';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  draft: 'Draft', under_investigation: 'Under investigation', risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval', approved_not_breach: 'Not a breach', approved_breach: 'Breach',
  notification_complete: 'Notification complete', closed: 'Closed',
};

const SUBS = [
  { id: 'workplan', label: 'Work plan', sub: 'Active incidents' },
  { id: 'dashboard', label: 'Dashboard', sub: 'Stats and trends' },
  { id: 'closed-search', label: 'Search all cases', sub: 'Open and closed, with filters' },
  { id: 'law-library', label: 'Law library', sub: 'State breach-notice laws' },
];

export default function IncidentsModule({ initialIncidentId = null, onConsumedInitial }) {
  const [view, setView] = useState(initialIncidentId ? 'detail' : 'workplan');
  const [selectedIncidentId, setSelectedIncidentId] = useState(initialIncidentId);
  // Opened from the map ("Open the case"): land on that case, once.
  useEffect(() => {
    if (initialIncidentId) { setSelectedIncidentId(initialIncidentId); setView('detail'); onConsumedInitial?.(); }
  }, [initialIncidentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [closedSearch, setClosedSearch] = useState('');
  const [searchStatus, setSearchStatus] = useState('all');
  const [closedResults, setClosedResults] = useState([]);
  const [closedSearching, setClosedSearching] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const openIncident = (id) => { setSelectedIncidentId(id); setView('detail'); };
  const backToList = () => { setSelectedIncidentId(null); setView('workplan'); setRefreshKey((k) => k + 1); };

  const searchAll = async () => {
    setClosedSearching(true);
    try {
      const params = new URLSearchParams({ all: '1' });
      if (closedSearch.trim()) params.set('search', closedSearch.trim());
      if (searchStatus !== 'all') params.set('status', searchStatus);
      const res = await fetch(`${API}/api/privacy/incidents?${params.toString()}`, { credentials: 'include' });
      const data = await res.json();
      setClosedResults(data.success ? (data.incidents || []) : []);
    } catch { setClosedResults([]); }
    finally { setClosedSearching(false); }
  };
  useEffect(() => { if (view === 'closed-search') searchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view, searchStatus]);

  const current = view === 'detail' ? 'workplan' : view;

  return (
    <div className="h-full min-h-0 flex flex-col">
      {view !== 'detail' && (
        <div className="px-8 pt-5 pb-3 flex items-center gap-2 flex-wrap">
          {SUBS.map((s) => (
            <button key={s.id} type="button" onClick={() => setView(s.id)} title={s.sub}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${current === s.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {s.label}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-400">HIPAA breach lifecycle · 45 CFR 164</span>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        {view === 'detail' && selectedIncidentId && (
          <PrivacyIncidentDetail incidentId={selectedIncidentId} onBack={backToList} onRefresh={() => setRefreshKey((k) => k + 1)} />
        )}
        {view === 'dashboard' && <PrivacyDashboard onOpenIncident={openIncident} />}
        {view === 'law-library' && <PrivacyLawLibrary />}
        {view === 'workplan' && <PrivacyWorkPlan key={refreshKey} onOpenIncident={openIncident} />}
        {view === 'closed-search' && (
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl font-semibold text-slate-900 tracking-tight mb-2">Search all cases</h2>
              <p className="text-slate-600 text-sm mb-6">Every incident, open or closed. Filter by status and search by title or summary. Open a case to view the full record or add addenda if allowed.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-slate-700 bg-white shadow-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400">
                  <option value="all">All statuses</option>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input type="text" value={closedSearch} onChange={(e) => setClosedSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchAll()} placeholder="Search by title or summary..."
                  className="flex-1 min-w-[220px] rounded-xl border border-slate-200 px-4 py-3 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400 transition-shadow" />
                <button type="button" onClick={searchAll} disabled={closedSearching} className="px-5 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium shadow-sm hover:opacity-90 disabled:opacity-50 transition-all">
                  {closedSearching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {closedResults.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200"><tr>
                      <th className="text-left py-3.5 px-5 font-semibold text-slate-700 w-24">ID</th><th className="text-left py-3.5 px-5 font-semibold text-slate-700">Title</th><th className="text-left py-3.5 px-5 font-semibold text-slate-700">Discovery</th><th className="text-left py-3.5 px-5 font-semibold text-slate-700">Status</th><th className="text-left py-3.5 px-5 font-semibold text-slate-700">Action</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {closedResults.map((inc) => (
                        <tr key={inc.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-3.5 px-5 text-slate-500 font-mono text-xs">{inc.id ? String(inc.id).slice(0, 8) : '—'}</td>
                          <td className="py-3.5 px-5 text-slate-900 font-medium">{inc.title || '—'}</td>
                          <td className="py-3.5 px-5 text-slate-600">{inc.discovery_date || '—'}</td>
                          <td className="py-3.5 px-5"><span className="inline-flex px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700">{STATUS_LABELS[inc.status] || inc.status || '—'}</span></td>
                          <td className="py-3.5 px-5"><button type="button" onClick={() => openIncident(inc.id)} className="text-orange-700 font-medium hover:underline">View</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!closedSearching && closedResults.length === 0 && <p className="text-slate-500 text-sm">No cases match these filters.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
